import { Button, Card, Container, Divider, Group, Table, Text } from "@mantine/core";
import { useUser } from "../tools/Context";
import downloadFromBackend from "./DownloadFromBackend";

function formatDate(inputDate) {
  if (!inputDate) return "";
  const date = new Date(inputDate);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear().toString().slice(-2);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} à ${hours}:${minutes}`;
}

export default function DisplayOsProof({ osData }) {
  const user = useUser();
  const isAdmin = user.role === "administrateur";
  const primaryColor = user.role === "titulaire de marché" ? "#ff7e2e" : "#0a0082";

  const workflow = Array.isArray(osData?.workflow)
    ? [...osData.workflow].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
    : [];

  const requiredSigners = workflow
    .filter((w) => w.role === "signer")
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
  const lastRequired = requiredSigners.length > 0 ? requiredSigners[requiredSigners.length - 1] : null;
  const lastRequiredKey = lastRequired ? `${lastRequired.signer_id}#${lastRequired.order_index}` : null;

  const canDownloadOs = Boolean(osData?.os_path);
  const canSeeGlobalProof = Boolean(osData?.sign_proof) && isAdmin;

  return (
    <Container size="md">
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between">
          <Text>
            {osData?.os_type} {osData?.os_name}
          </Text>
          <Group>
            {canDownloadOs && (
              <Button color={primaryColor} variant="light" onClick={() => downloadFromBackend(osData.os_path)}>
                Télécharger l'OS
              </Button>
            )}
            {canSeeGlobalProof && (
              <Button
                component="a"
                color={primaryColor}
                href={`${process.env.REACT_APP_DATALOCK_URL_WEB}proof?uid=${osData.sign_proof}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Voir la preuve
              </Button>
            )}
          </Group>
        </Group>

        <Divider mt={15} mb={15} />

        <Table striped highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Utilisateur</Table.Th>
              <Table.Th>Rôle</Table.Th>
              <Table.Th>Date</Table.Th>
              <Table.Th>Statut</Table.Th>
              <Table.Th>Réserve</Table.Th>
              {isAdmin && <Table.Th>Preuve</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {workflow.length > 0 ? (
              workflow.map((w, idx) => {
                const isViewer = w.role === "viewer";
                const isSigned = Boolean(w.signed);
                const status = w.status ?? (isViewer ? "" : isSigned ? "approved" : "pending");

                const thisKey = `${w.signer_id}#${w.order_index}`;
                const isLastRequiredSigner = !isViewer && lastRequiredKey && thisKey === lastRequiredKey;

                let statusLabel = "";
                if (isViewer) {
                  statusLabel = "Lecteur";
                } else if (status === "rejected") {
                  statusLabel = "Rejeté";
                } else if (isLastRequiredSigner && isSigned) {
                  statusLabel = "Accusé de réception";
                } else if (status === "signé avec réserve") {
                  statusLabel = "Signé avec réserve";
                } else if (isSigned) {
                  statusLabel = "Signé";
                } else {
                  statusLabel = "";
                }

                return (
                  <Table.Tr key={idx}>
                    <Table.Td>{w.email || w.user_email || "–"}</Table.Td>
                    <Table.Td>{isViewer ? "Lecteur" : "Signataire"}</Table.Td>
                    <Table.Td>{w.signed_at ? formatDate(w.signed_at) : ""}</Table.Td>
                    <Table.Td>{statusLabel}</Table.Td>
                    <Table.Td>{w.comment || ""}</Table.Td>
                    {isAdmin && (
                      <Table.Td>
                        {w.cepaj_id ? (
                          <Button
                            component="a"
                            color={primaryColor}
                            href={`${process.env.REACT_APP_DATALOCK_URL_WEB}proof?uid=${w.cepaj_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Voir la preuve
                          </Button>
                        ) : (
                          ""
                        )}
                      </Table.Td>
                    )}
                  </Table.Tr>
                );
              })
            ) : (
              <Table.Tr>
                <Table.Td colSpan={isAdmin ? 6 : 5} style={{ textAlign: "center" }}>
                  Aucun historique de signature disponible.
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>

        {Array.isArray(osData?.uploadedFiles) && osData.uploadedFiles.length > 0 && (
          <>
            <Divider mt={15} mb={15} />
            <Text>Documents associés</Text>
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Nom</Table.Th>
                  <Table.Th>Télécharger</Table.Th>
                  {osData.documment_linked_proof && isAdmin && <Table.Th>Preuve</Table.Th>}
                  <Table.Th>Ajouté le</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {osData.uploadedFiles.map((file, index) => (
                  <Table.Tr key={index}>
                    <Table.Td>{file.name}</Table.Td>
                    <Table.Td>
                      <Button color={primaryColor} variant="light" onClick={() => downloadFromBackend(file.id)}>
                        Télécharger
                      </Button>
                    </Table.Td>
                    {osData.documment_linked_proof && isAdmin && (
                      <Table.Td>
                        <Button
                          component="a"
                          color={primaryColor}
                          href={`${process.env.REACT_APP_DATALOCK_URL_WEB}proof?uid=${osData.documment_linked_proof}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Voir la preuve
                        </Button>
                      </Table.Td>
                    )}
                    <Table.Td>{file.date ? `Ajouté le ${formatDate(file.date)}` : ""}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </>
        )}
      </Card>
    </Container>
  );
}
